import { ApolloClient } from "@apollo/client";
import {
  GetAccountingSystemInvoicesGroupedParams,
  IAccountingSystemInvoiceRepository,
  IGetAccountingSystemInvoicesGroupedRepositoryResponse,
  IGetAccountingSystemInvoicesRepositoryResponse,
} from "@/core/repositories/accounting";
import { AccountingSystemInvoice } from "@/core/entities/accounting";
import GET_ACCOUNTING_SYSTEM_INVOICES from "@/core/graphql/accounting/getAccountingSystemInvoices";
import GET_ACCOUNTING_SYSTEM_INVOICES_GROUPED from "@/core/graphql/accounting/getAccountingSystemInvoicesGrouped";

interface IGetAccountingSystemInvoicesQueryResponse {
  getAccountingSystemInvoices: {
    accountingSystemKey: string;
    total: number;
    items: AccountingSystemInvoice[];
  };
}

interface IGetAccountingSystemInvoicesGroupedQueryResponse {
  getAccountingSystemInvoicesGrouped: {
    accountingSystemKey: string;
    linkedCount: number;
    unlinkedCount: number;
    total: number;
    data: {
      linkedToCheck: AccountingSystemInvoice[];
      unlinked: AccountingSystemInvoice[];
    };
  };
}

const mapAccountingSystemInvoice = (accountingSystemInvoice: AccountingSystemInvoice): AccountingSystemInvoice =>
  new AccountingSystemInvoice(accountingSystemInvoice);

export class AccountingSystemInvoiceRepository implements IAccountingSystemInvoiceRepository {
  constructor(private readonly client: ApolloClient) {}

  public async getAccountingSystemInvoices(
    refetch: boolean = false
  ): Promise<IGetAccountingSystemInvoicesRepositoryResponse> {
    const { data } = await this.client.query<IGetAccountingSystemInvoicesQueryResponse>({
      query: GET_ACCOUNTING_SYSTEM_INVOICES,
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });
    if (!data) {
      throw new Error("Failed to get accounting system invoices");
    }
    return {
      getAccountingSystemInvoices: {
        accountingSystemKey: data.getAccountingSystemInvoices.accountingSystemKey,
        total: data.getAccountingSystemInvoices.total,
        items: data.getAccountingSystemInvoices.items.map(mapAccountingSystemInvoice),
      },
    };
  }

  public async getAccountingSystemInvoicesGrouped(
    params: GetAccountingSystemInvoicesGroupedParams,
    refetch: boolean = false
  ): Promise<IGetAccountingSystemInvoicesGroupedRepositoryResponse> {
    const { data } = await this.client.query<IGetAccountingSystemInvoicesGroupedQueryResponse>({
      query: GET_ACCOUNTING_SYSTEM_INVOICES_GROUPED,
      variables: {
        params: {
          check_number: params.checkNumber,
          purchaser_key: params.purchaserKey,
        },
      },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });
    if (!data) {
      throw new Error("Failed to get grouped accounting system invoices");
    }

    const grouped = data.getAccountingSystemInvoicesGrouped;
    return {
      getAccountingSystemInvoicesGrouped: {
        accountingSystemKey: grouped.accountingSystemKey,
        linkedCount: grouped.linkedCount,
        unlinkedCount: grouped.unlinkedCount,
        total: grouped.total,
        data: {
          linkedToCheck: grouped.data.linkedToCheck.map(mapAccountingSystemInvoice),
          unlinked: grouped.data.unlinked.map(mapAccountingSystemInvoice),
        },
      },
    };
  }
}
